import { Injectable } from '@angular/core';
import { Observable, from, switchMap, catchError } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { handleErrorFunction } from '../../../shared/helpers/handleError';
import { EntraService } from '../../../service/entra.service';
import { SofStorageFacility } from '../../../models/sof-receiving/SofStorageFacility.interface';
import { SofReceivingOrder } from '../../../models/sof-receiving/SofReceivingOrder.interface';
import { SofOrderDetails } from '../../../models/sof-receiving/SofOrderDetails.interface';
import { SofPoNumber } from '../../../models/sof-receiving/SofPoNumber.interface';
import { SofReceivingLogItem } from '../../../models/sof-receiving/SofReceivingLogItem.interface';
import {
  SofReceivingLogUpdateRequest,
  SofReceivingLogUpdateResult,
} from '../../../models/sof-receiving/SofReceivingLogUpdate.interface';
import { SofStagingAreaUpdateRequest } from '../../../models/sof-receiving/SofStagingAreaUpdate.interface';
import {
  SofStatusUpdateRequest,
  SofOperationResponse,
} from '../../../models/sof-receiving/SofStatusUpdate.interface';
import {
  SofPhotoMetadata,
  SofPhotoResponse,
} from '../../../models/sof-receiving/SofPhoto.interface';

/**
 * Pure HTTP client for the SOF Receiving backend endpoints (V1/sof-receiving).
 *
 * Every method acquires a bearer token from EntraService, makes the request,
 * and returns an Observable. No state, no toasts, no loading flags — the
 * SofReceivingStateService owns those concerns. Errors are normalized through
 * handleErrorFunction into `{ message, details, originalError }` objects.
 *
 * SOF / PO / storage-facility values are free-form strings, so they are always
 * sent as query parameters (HttpParams handles the encoding), never as route
 * segments.
 */
@Injectable({
  providedIn: 'root',
})
export class SofReceivingApiService {
  private readonly _baseUrl = `${environment.apiUrl}/V1/sof-receiving`;

  constructor(
    private _httpClient: HttpClient,
    private _entraService: EntraService
  ) {}

  /**
   * Acquires a token and runs the given request with Authorization headers,
   * piping errors through the centralized error handler.
   */
  private authorizedRequest<T>(
    request: (headers: HttpHeaders) => Observable<T>
  ): Observable<T> {
    return from(this._entraService.acquireToken()).pipe(
      switchMap((accessToken) => {
        const headers: HttpHeaders = new HttpHeaders({
          Authorization: `Bearer ${accessToken}`,
        });
        return request(headers).pipe(
          catchError((error) => handleErrorFunction(error))
        );
      })
    );
  }

  /** GET storage-facilities — all facility names (mapping validation only). */
  public getStorageFacilities(): Observable<SofStorageFacility[]> {
    return this.authorizedRequest((headers) =>
      this._httpClient.get<SofStorageFacility[]>(
        `${this._baseUrl}/storage-facilities`,
        { headers }
      )
    );
  }

  /** GET orders — active receiving orders for a storage facility. */
  public getOrders(storageFacility: string): Observable<SofReceivingOrder[]> {
    const params = new HttpParams().set('storageFacility', storageFacility);
    return this.authorizedRequest((headers) =>
      this._httpClient.get<SofReceivingOrder[]>(`${this._baseUrl}/orders`, {
        headers,
        params,
      })
    );
  }

  /** GET orders/history — completed/historical orders for a storage facility. */
  public getOrdersHistory(
    storageFacility: string
  ): Observable<SofReceivingOrder[]> {
    const params = new HttpParams().set('storageFacility', storageFacility);
    return this.authorizedRequest((headers) =>
      this._httpClient.get<SofReceivingOrder[]>(
        `${this._baseUrl}/orders/history`,
        { headers, params }
      )
    );
  }

  /** GET sof — the SOF order details, or null when not found. */
  public getSofDetails(sof: string): Observable<SofOrderDetails | null> {
    const params = new HttpParams().set('sof', sof);
    return this.authorizedRequest((headers) =>
      this._httpClient.get<SofOrderDetails | null>(`${this._baseUrl}/sof`, {
        headers,
        params,
      })
    );
  }

  /** GET pos — the POs attached to a SOF. */
  public getPos(sof: string): Observable<SofPoNumber[]> {
    const params = new HttpParams().set('sof', sof);
    return this.authorizedRequest((headers) =>
      this._httpClient.get<SofPoNumber[]>(`${this._baseUrl}/pos`, {
        headers,
        params,
      })
    );
  }

  /** GET items — the receiving-log items for a SOF's PO. */
  public getItems(sof: string, po: string): Observable<SofReceivingLogItem[]> {
    const params = new HttpParams().set('sof', sof).set('po', po);
    return this.authorizedRequest((headers) =>
      this._httpClient.get<SofReceivingLogItem[]>(`${this._baseUrl}/items`, {
        headers,
        params,
      })
    );
  }

  /** PUT items — batch checkbox/notes update; returns per-item results. */
  public updateItems(
    request: SofReceivingLogUpdateRequest
  ): Observable<SofReceivingLogUpdateResult[]> {
    return this.authorizedRequest((headers) =>
      this._httpClient.put<SofReceivingLogUpdateResult[]>(
        `${this._baseUrl}/items`,
        request,
        { headers }
      )
    );
  }

  /** PUT staging-area — updates the SOF's free-text staging area. */
  public updateStagingArea(
    request: SofStagingAreaUpdateRequest
  ): Observable<SofOperationResponse> {
    return this.authorizedRequest((headers) =>
      this._httpClient.put<SofOperationResponse>(
        `${this._baseUrl}/staging-area`,
        request,
        { headers }
      )
    );
  }

  /** POST in-progress — marks receiving In Progress for a SOF/PO. */
  public setInProgress(
    request: SofStatusUpdateRequest
  ): Observable<SofOperationResponse> {
    return this.authorizedRequest((headers) =>
      this._httpClient.post<SofOperationResponse>(
        `${this._baseUrl}/in-progress`,
        request,
        { headers }
      )
    );
  }

  /**
   * POST complete — marks receiving Complete for a SOF/PO. The server rejects
   * with 400 when the SOF has no staging area set.
   */
  public setComplete(
    request: SofStatusUpdateRequest
  ): Observable<SofOperationResponse> {
    return this.authorizedRequest((headers) =>
      this._httpClient.post<SofOperationResponse>(
        `${this._baseUrl}/complete`,
        request,
        { headers }
      )
    );
  }

  /**
   * POST photos — uploads proof photos for a SOF as multipart form data
   * (files under 'files', the SOF number under 'sof'; the browser sets the
   * multipart boundary — do not set Content-Type manually).
   */
  public uploadPhotos(
    sof: string,
    files: File[]
  ): Observable<SofPhotoMetadata[]> {
    return this.authorizedRequest((headers) => {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file, file.name));
      formData.append('sof', sof);
      return this._httpClient.post<SofPhotoMetadata[]>(
        `${this._baseUrl}/photos`,
        formData,
        { headers }
      );
    });
  }

  /** GET photos — the SOF's proof photos, each with a 1-hour SAS url. */
  public getPhotos(sof: string): Observable<SofPhotoResponse> {
    const params = new HttpParams().set('sof', sof);
    return this.authorizedRequest((headers) =>
      this._httpClient.get<SofPhotoResponse>(`${this._baseUrl}/photos`, {
        headers,
        params,
      })
    );
  }

  /**
   * DELETE photos/{photoId} — deletes one proof photo. The server rejects
   * with 400 once the SOF's receiving is Complete.
   */
  public deletePhoto(photoId: number): Observable<SofOperationResponse> {
    return this.authorizedRequest((headers) =>
      this._httpClient.delete<SofOperationResponse>(
        `${this._baseUrl}/photos/${photoId}`,
        { headers }
      )
    );
  }
}
